import React, { useState, useEffect } from 'react';
import { Modal, Button, Spinner, Alert, Badge, Image, Row, Col, Card } from 'react-bootstrap';
import { ExternalLink, Package, Copy } from 'lucide-react';
import { skinSeoulProductsClient } from '../../../../infrastructure/SkinSeoulProductsClient';
import type { SkinSeoulAdminProduct, PublicationStatus } from '../../../../models/ProductTypes';
import { getPublicationStatusColor, getStockStatusColor } from '../../../../utils/statusUtils';

interface ProductDetailsModalProps {
  show: boolean;
  onHide: () => void;
  sku: string;
  postStatus?: PublicationStatus | undefined;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) {
    return '-';
  }

  try {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

const copyToClipboard = async (text: string, label: string) => {
  try {
    await navigator.clipboard.writeText(text);
    console.info(`${label} copied to clipboard: ${text}`);
  } catch (error) {
    console.error(`Failed to copy ${label}:`, error);
  }
};

export const ProductDetailsModal: React.FC<ProductDetailsModalProps> = ({ show, onHide, sku, postStatus }) => {
  const [product, setProduct] = useState<SkinSeoulAdminProduct | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (show && sku) {
      fetchProductDetails();
    }
  }, [show, sku, postStatus]);

  const fetchProductDetails = async () => {
    setLoading(true);
    setError(null);
    setProduct(null);

    try {
      // Query the admin API with the SKU and postStatus
      const response = await skinSeoulProductsClient.getProductsBySku([sku], postStatus);

      if (response && response.length > 0 && response[0]) {
        setProduct(response[0]);
      } else {
        setProduct(null);
        setError('Product not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch product details');
    } finally {
      setLoading(false);
    }
  };

  const renderImageGallery = (product: SkinSeoulAdminProduct) => {
    const images = product.productImageUrls || [];
    const learnMoreUrls = product.attributes?.['learn_more_url']
      ? Array.isArray(product.attributes['learn_more_url'])
        ? product.attributes['learn_more_url']
        : [product.attributes['learn_more_url']]
      : [];

    if (images.length === 0 && learnMoreUrls.length === 0) {
      return (
        <div className="text-center py-4">
          <Package size={48} className="text-muted mb-2" />
          <p className="text-muted">No images available</p>
        </div>
      );
    }

    return (
      <div>
        {images.length > 0 && (
          <div className="mb-3">
            <h6 className="mb-2">Product Images ({images.length})</h6>
            <Row className="g-2">
              {images.map((url, index) => (
                <Col key={index} xs={6} md={4} lg={3}>
                  <Image
                    src={url}
                    alt={`Product image ${index + 1}`}
                    fluid
                    rounded
                    style={{
                      height: '120px',
                      objectFit: 'cover',
                      cursor: 'pointer',
                    }}
                    onClick={() => window.open(url, '_blank')}
                  />
                </Col>
              ))}
            </Row>
          </div>
        )}

        {learnMoreUrls.length > 0 && (
          <div>
            <h6 className="mb-2">PDP Images ({learnMoreUrls.length})</h6>
            <Row className="g-2">
              {learnMoreUrls.map((url, index) => (
                <Col key={index} xs={6} md={4} lg={3}>
                  <Image
                    src={url}
                    alt={`PDP image ${index + 1}`}
                    fluid
                    rounded
                    style={{
                      height: '120px',
                      objectFit: 'cover',
                      cursor: 'pointer',
                    }}
                    onClick={() => window.open(url, '_blank')}
                  />
                </Col>
              ))}
            </Row>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Product Details - SKU: {sku}</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ maxHeight: '80vh', overflowY: 'auto' }}>
        {loading && (
          <div className="text-center py-4">
            <Spinner animation="border" variant="primary" />
            <div className="mt-2">Loading product details...</div>
          </div>
        )}

        {error && (
          <Alert variant="danger">
            <strong>Error:</strong> {error}
          </Alert>
        )}

        {!loading && !error && product && (
          <div>
            {/* Product Header */}
            <Card className="mb-3">
              <Card.Body>
                <Row>
                  <Col md={3}>
                    {product.productImageUrls && product.productImageUrls.length > 0 ? (
                      <Image
                        src={product.productImageUrls[0]}
                        alt={product.name}
                        fluid
                        rounded
                        style={{ height: '200px', objectFit: 'cover' }}
                      />
                    ) : (
                      <div
                        className="d-flex align-items-center justify-content-center bg-light text-muted"
                        style={{ height: '200px', borderRadius: '8px' }}
                      >
                        <Package size={48} />
                      </div>
                    )}
                  </Col>
                  <Col md={9}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <h5 className="mb-0">{product.name}</h5>
                      <div className="d-flex gap-2">
                        <Badge bg={getPublicationStatusColor(product.postStatus!)}>{product.postStatus}</Badge>
                        <Badge bg={getStockStatusColor(product.status!)}>{product.status}</Badge>
                      </div>
                    </div>

                    {product.nameKor && <p className="text-muted mb-2">{product.nameKor}</p>}

                    <div className="mb-2">
                      <strong>Brand:</strong> {product.brand} |<strong className="ms-2">Category:</strong>{' '}
                      {product.category.join(' › ')}
                    </div>

                    <div className="mb-2">
                      <strong>Size:</strong> {product.size || '-'} |<strong className="ms-2">Pack:</strong>{' '}
                      {product.pack || '-'}
                      {product.attributes?.['attribute_color'] && (
                        <>
                          {' | '}
                          <strong className="ms-2">Color:</strong> {product.attributes['attribute_color']}
                        </>
                      )}
                    </div>

                    <div className="mb-2">
                      <strong>Price:</strong> {product.originalPrice} {product.currency}
                    </div>

                    <div className="mb-2">
                      <strong>Modified:</strong> {formatDate(product.modifiedAt?.toString())}
                    </div>

                    {/* Action Links */}
                    <div className="d-flex gap-2 flex-wrap">
                      {product.productLink && (
                        <a
                          href={product.productLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-primary btn-sm"
                          style={{ wordBreak: 'break-all' }}
                        >
                          <ExternalLink size={14} className="me-1" />
                          {product.productLink}
                        </a>
                      )}
                      {product.sourceLink && (
                        <a
                          href={product.sourceLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-outline-secondary btn-sm"
                        >
                          <ExternalLink size={14} className="me-1" />
                          Coupang Source
                        </a>
                      )}
                    </div>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            {/* Product Details */}
            <Row>
              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Product Information</h6>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-2">
                      <strong>Product ID:</strong>
                      <span
                        className="ms-2 text-primary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => copyToClipboard(product.productId!, 'Product ID')}
                        title="Click to copy"
                      >
                        {product.productId}
                        <Copy size={12} className="ms-1" />
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong>SKU:</strong>
                      <span
                        className="ms-2 text-primary"
                        style={{ cursor: 'pointer' }}
                        onClick={() => copyToClipboard(product.sku, 'SKU')}
                        title="Click to copy"
                      >
                        {product.sku}
                        <Copy size={12} className="ms-1" />
                      </span>
                    </div>
                    <div className="mb-2">
                      <strong>Type:</strong> {product.type}
                    </div>
                    {product.description && (
                      <div>
                        <strong>Description:</strong>
                        <p className="mt-1 text-muted">{product.description}</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="mb-3">
                  <Card.Header>
                    <h6 className="mb-0">Attributes</h6>
                  </Card.Header>
                  <Card.Body style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {product.attributes && Object.keys(product.attributes).length > 0 ? (
                      Object.entries(product.attributes).map(([key, value]) => (
                        <div key={key} className="mb-2 pb-2 border-bottom">
                          <strong style={{ fontSize: '0.85rem' }}>{key}:</strong>
                          <div style={{ fontSize: '0.85rem', wordBreak: 'break-word' }}>
                            {typeof value === 'string' && value.length > 100 ? `${value.substring(0, 100)}...` : value}
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-muted">No attributes available</p>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* Images Section */}
            <Card>
              <Card.Header>
                <h6 className="mb-0">Images</h6>
              </Card.Header>
              <Card.Body>{renderImageGallery(product)}</Card.Body>
            </Card>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
